import { Container, Group, Title, Text, SimpleGrid, Card, Table, Badge, Progress, Skeleton } from '@mantine/core';
import { useFFVPSummary, useFFVPOrders } from '@/hooks/usePortalApi';

const STATUS_COLORS: Record<string, string> = {
  pending: 'yellow',
  approved: 'blue',
  delivered: 'green',
  cancelled: 'red',
};

export default function FFVP() {
  const { data: summary, isLoading: summaryLoading } = useFFVPSummary();
  const { data: orders, isLoading: ordersLoading } = useFFVPOrders();

  const fmt = (n: number) => '$' + n.toFixed(2);

  const usagePercentage = summary ? (summary.amount_used / summary.total_allocation) * 100 : 0;

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <div>
          <Title order={2}>Fresh Fruit & Vegetable Program</Title>
          <Text c="dimmed" size="sm">USDA FFVP allocation and usage tracking</Text>
        </div>
      </Group>

      {summaryLoading ? (
        <SimpleGrid cols={3} mb="xl">
          {[1, 2, 3].map((i) => (
            <Card key={i} shadow="sm" padding="lg" withBorder>
              <Skeleton height={20} width="60%" mb="xs" />
              <Skeleton height={30} width="40%" />
            </Card>
          ))}
        </SimpleGrid>
      ) : summary ? (
        <>
          <SimpleGrid cols={3} mb="xl">
            <Card shadow="sm" padding="lg" withBorder>
              <Text size="sm" c="dimmed" mb="xs">Total Allocation</Text>
              <Text size="xl" fw={700}>{fmt(summary.total_allocation)}</Text>
            </Card>

            <Card shadow="sm" padding="lg" withBorder>
              <Text size="sm" c="dimmed" mb="xs">Amount Used</Text>
              <Text size="xl" fw={700} c="blue">{fmt(summary.amount_used)}</Text>
            </Card>

            <Card shadow="sm" padding="lg" withBorder>
              <Text size="sm" c="dimmed" mb="xs">Remaining Balance</Text>
              <Text size="xl" fw={700} c="green">{fmt(summary.remaining_balance)}</Text>
            </Card>
          </SimpleGrid>

          <Card shadow="sm" padding="lg" withBorder mb="xl">
            <Text size="sm" mb="xs">Allocation Usage</Text>
            <Progress
              value={usagePercentage}
              size="lg"
              color={usagePercentage > 90 ? 'red' : usagePercentage > 75 ? 'yellow' : 'blue'}
            />
            <Text size="xs" c="dimmed" mt="xs">
              {usagePercentage.toFixed(1)}% of allocation used
            </Text>
          </Card>
        </>
      ) : null}

      <Card shadow="sm" padding="lg" withBorder>
        <Title order={4} mb="md">FFVP Orders</Title>
        {ordersLoading ? (
          <div>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height={40} mb="sm" />
            ))}
          </div>
        ) : !orders || orders.length === 0 ? (
          <Text ta="center" c="dimmed" py="md">No FFVP orders found</Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Order Date</Table.Th>
                <Table.Th>Items</Table.Th>
                <Table.Th>Amount</Table.Th>
                <Table.Th>School/Site</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {orders.map((order: any) => (
                <Table.Tr key={order.id}>
                  <Table.Td>{new Date(order.order_date).toLocaleDateString()}</Table.Td>
                  <Table.Td>{order.items}</Table.Td>
                  <Table.Td>{fmt(order.amount)}</Table.Td>
                  <Table.Td>{order.school_site}</Table.Td>
                  <Table.Td>
                    <Badge color={STATUS_COLORS[order.status]} variant="light">
                      {order.status}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>
    </Container>
  );
}
